import apiClient from '../apiClient';
import type { ApiResponse, Company, PaginationMeta } from '@/types';
import { getUrlWithQueryParams } from '@/lib/utils';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactQueryKeys } from '../reactQueryKeys';
import { ReactMutationKeys } from '../reactMutationKeys';

const BASE_URL = '/dashboard/companies';
const ADMIN_URL = '/admin/companies';

interface PaginatedResponse {
  items: Company[];
  pagination: PaginationMeta;
}

export const getCompanies = async (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  status?: string
): Promise<PaginatedResponse> => {
  const url = getUrlWithQueryParams(ADMIN_URL, {
    page,
    limit,
    search,
    sortBy,
    sortOrder,
    status,
  });
  const { data } = await apiClient.get<ApiResponse<Company[]>>(url);
  return {
    items: data.data,
    pagination: data.meta!,
  };
};

export const getCompany = async (id: string): Promise<Company> => {
  const { data } = await apiClient.get<ApiResponse<Company>>(`${ADMIN_URL}/${id}`);
  return data.data;
};

export const createCompany = async (company: Partial<Company>): Promise<Company> => {
  const { data } = await apiClient.post<ApiResponse<Company>>(ADMIN_URL, company);
  return data.data;
};

export const updateCompany = async (id: string, company: Partial<Company>): Promise<Company> => {
  const { data } = await apiClient.put<ApiResponse<Company>>(`${ADMIN_URL}/${id}`, company);
  return data.data;
};

export const deleteCompany = async (id: string): Promise<void> => {
  await apiClient.delete(`${ADMIN_URL}/${id}`);
};

// React Query Hooks

export const useGetCompanies = (
  page?: number,
  limit?: number,
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  status?: string
) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_COMPANIES, page, limit, search, sortBy, sortOrder, status],
    queryFn: () => getCompanies(page, limit, search, sortBy, sortOrder, status),
  });

export const useGetCompany = (id: string) =>
  useQuery({
    queryKey: [ReactQueryKeys.GET_COMPANY, id],
    queryFn: () => getCompany(id),
    enabled: !!id,
  });

export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (company: Partial<Company>) => createCompany(company),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_COMPANIES] });
    },
    mutationKey: [ReactMutationKeys.CREATE_COMPANY],
  });
};

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...company }: Partial<Company> & { id: string }) =>
      updateCompany(id, company),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_COMPANIES] });
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_COMPANY] });
    },
    mutationKey: [ReactMutationKeys.UPDATE_COMPANY],
  });
};

export const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ReactQueryKeys.GET_COMPANIES] });
    },
    mutationKey: [ReactMutationKeys.DELETE_COMPANY],
  });
};
